import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import type { ContactJson } from 'app/types/entities'
import { notifyError } from 'app/services/notify.client'
import { useHandleSearchParams } from 'app/utils'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { hiddenFromParam, hiddenFromRules } from 'lib/treatmentBooked'
import type { TreatmentBandId } from './treatmentBands'
import type { TreatmentCard } from './treatmentFixture'
import { EXIT_DECLINED } from './treatmentStageCatalog'
import { useContactMessages } from './useContactMessages'
import { type BoardSort, type TreatmentRow, useTreatmentBoard } from './useTreatmentBoard'
import { type BandCardsOptions, readCase } from './useBandCards'

/** The whole contact. A case card carries nine fields; the drawer and the thread read all of them. */
export async function readContact(
  contactId: string,
  options?: Parameters<typeof gqlFetchOne>[3],
): Promise<ContactJson | undefined> {
  try {
    const contact = await gqlFetchOne<ContactJson>(`contact`, contactId, undefined, options)
    if (!contact?.id) throw new Error(`No contact came back`)
    return contact
  } catch (err) {
    logError(`Could not open this patient's record`, err)
    return undefined
  }
}

/**
 * The treatment cases a page reads, and the case dialog over them.
 *
 * Shared by the Unscheduled Treatment list and the Treatment Plans page, which differ only in
 * which cases they read (a band or all of them) and in where a patient's record opens.
 * `openRecord` says whether the record opened; only then does the dialog close behind it.
 */
export function useTreatmentCaseBoard({
  sort,
  direction,
  band,
  rule,
  hideExits,
  openRecord,
}: {
  sort: BoardSort
  direction: `ASC` | `DESC`
  band?: TreatmentBandId
  /** A saved treatment-plan list's rule, laid under the page's own filters. */
  rule?: BandCardsOptions[`rule`]
  hideExits?: boolean
  openRecord: (contactId: string) => boolean | Promise<boolean>
}) {
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const hideParam = searchParams.get(`hide`)
  const rulesKey = [`visit`, `visitOp`, `seen`, `hide`].map((key) => searchParams.get(key)).join(`|`)
  const hidden = useMemo(() => hiddenFromRules(searchParams), [rulesKey])

  // A link from before the Filters rules carries `hide`. It is rewritten into the rules once, so
  // the builder shows the narrowing it makes; `hiddenFromRules` reads it until then.
  useEffect(() => {
    if (hideParam === null) return
    const groups = hiddenFromParam(hideParam)
    const visit = groups.filter((group) => group !== `unseen`)
    handleSearchParams(
      {
        hide: undefined,
        visit: visit.length ? visit.join(`,`) : undefined,
        visitOp: visit.length ? `not_in` : undefined,
        seen: groups.includes(`unseen`) ? `yes` : undefined,
      },
      { replace: true },
    )
  }, [hideParam])

  const board = useTreatmentBoard({ sort, direction, band, hidden, rule, hideExits })

  // The open case lives in `?case=`, so a link or a reload opens the same dialog.
  const openId = searchParams.get(`case`) ?? undefined
  // Replace, not push: Back after closing goes to the page before the case, not back into it.
  const closeCase = useCallback(() => handleSearchParams({ case: undefined }, { replace: true }), [])
  /**
   * The case an exit list opened, when that case is not in the rows on screen.
   *
   * An exit list reads its whole stage, not the page of the band this board
   * has loaded, so it can hand over a case `rows` has never seen. The list
   * already holds the card it drew, so it passes that rather than the board
   * going looking for one it does not have.
   */
  const [exitCard, setExitCard] = useState<TreatmentCard | undefined>(undefined)
  const [opening, setOpening] = useState<string>()
  // The thread lives out here rather than in the dialog: the dialog unmounts
  // its body to turn the card over, and a hook that unmounts with it would
  // re-read the whole conversation on every flip.
  const [texting, setTexting] = useState<ContactJson>()
  const [threadOpen, setThreadOpen] = useState(false)
  const thread = useContactMessages(texting)

  const openContact = useCallback(
    async (contactId: string) => {
      setOpening(contactId)
      try {
        // The dialog closes behind the record: two overlays fighting for focus
        // is worse than one click to come back.
        if (await openRecord(contactId)) closeCase()
      } finally {
        setOpening(undefined)
      }
    },
    [openRecord, closeCase],
  )

  // The dialog turns over to the thread rather than handing off to the drawer.
  const startTexting = useCallback(
    async (contactId: string) => {
      // Already read once this session: turn straight over rather than refetch.
      if (texting?.id === contactId) return setThreadOpen(true)
      setOpening(contactId)
      const contact = await readContact(contactId)
      setOpening(undefined)
      if (contact) {
        setTexting(contact)
        setThreadOpen(true)
      }
    },
    [texting?.id],
  )

  useEffect(() => {
    setThreadOpen(false)
    setTexting(undefined)
  }, [openId])

  // Read off the flat rows rather than the columns: a case sitting at one of
  // the two exits is in `rows` and in no column, and the table can open one.
  // `exitCard` covers the rest: an exit list reaches past the loaded band.
  const openRow = board.rows.find((row) => row.id === openId)
  const open = openRow ?? (exitCard?.id === openId ? exitCard : undefined)
  const ready = board.sampleCount > 0 || !board.loading

  // A linked case the board has not read is read on its own, once the first page has had its
  // chance to hold it: the link may name somebody past the loaded pages or outside the band.
  const haveOpen = Boolean(open)
  useEffect(() => {
    if (!openId || haveOpen || !ready) return
    let current = true
    bg(
      readCase(openId).then(
        (card) => {
          if (!current) return
          if (card) return setExitCard(card)
          notifyError(`No unscheduled treatment found for this patient`)
          closeCase()
        },
        (err) => {
          logError(`Could not open this treatment case`, err)
          if (!current) return
          notifyError(`Couldn't open this treatment case`)
          closeCase()
        },
      ),
    )
    return () => {
      current = false
    }
  }, [openId, haveOpen, ready])

  // A case handed over by an exit list is at Scheduled or Lost, so it sits at
  // no working stage for the dialog to mark.
  const openStageName = open && (openRow?.stage ?? board.stageOf(open))
  const openStage = board.lanes.find((lane) => lane.label === openStageName)?.id
  // A case in the booked stage is read-only: only a real booking moves it, so the dialog offers no stage choices.
  const lockedStage = board.bookedStage && openStageName === board.bookedStage.name ? board.bookedStage.name : undefined

  const openCase = useCallback((card: TreatmentCard) => {
    setExitCard(card)
    handleSearchParams({ case: card.id })
  }, [])
  const openRowCase = useCallback((row: TreatmentRow) => handleSearchParams({ case: row.id }), [])
  const openRowRecord = useCallback((row: TreatmentRow) => openContact(row.id), [openContact])

  return {
    board,
    /** False until the first page lands, which is what draws the table's spinner. */
    ready,
    openCase,
    openRowCase,
    openRowRecord,
    caseDetail: {
      card: open,
      stage: openStage,
      stages: board.lanes.filter((lane) => lane.label !== board.bookedStage?.name),
      lockedStage,
      onClose: () => {
        closeCase()
        setExitCard(undefined)
        setThreadOpen(false)
        setTexting(undefined)
      },
      // The card goes along with every write: one opened from an exit list
      // can sit outside the pages the band has loaded, and the board cannot
      // move a case it has never read. Putting a case back on the board is
      // one of these, and since the chips went it is the only one.
      onMove: (laneId: string) => open && board.move(open.id, laneId, open),
      onSchedule: board.listBacked
        ? undefined
        : () => {
            if (open) board.markScheduled(open.id, open)
            closeCase()
          },
      onDecline:
        lockedStage || (board.listBacked && !board.exits.some((exit) => exit.label === EXIT_DECLINED))
          ? undefined
          : () => {
              if (open) board.decline(open.id, open)
              closeCase()
            },
      onOpenContact: () => open && openContact(open.id),
      onText: () => open && startTexting(open.id),
      opening: !!opening,
      texting,
      threadOpen,
      thread,
      onBackFromText: () => setThreadOpen(false),
    },
  }
}
